import { useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { Label } from '../../../shared/compass-ui'
import ControlledTextInput from '../../shared/ControlledTextInput'
import PhoneInput from '../../shared/PhoneInput'
import SuggestionText from '../../shared/SuggestionText'
import TextInputError from '../../shared/TextInputError'
import { FormValues } from '../../../shared/form/schema'
import { useAnalytics } from '../../../shared/context/Analytics'
import useFormStore from '../../../shared/hooks/useFormStore'
import calculateDomainSuggestion from '../../../shared/utils/calculateDomainSuggestion'

const ContactInformation = () => {
  const { track, events } = useAnalytics()
  const {
    clearErrors,
    setValue,
    setFocus,
    formState: { errors },
  } = useFormContext<FormValues>()
  const [formStore] = useFormStore()
  const { isMulticardFlow } = formStore
  const { emails: emailError, phoneNumber: phoneNumberError } = errors
  const [emailSuggestion, setEmailSuggestion] = useState<string>('')

  const handleEmailBlur = (email: string) => {
    const emailParts = email.split('@')
    if (emailParts.length === 2) {
      const suggestion = calculateDomainSuggestion(emailParts[1])
      if (suggestion) {
        setEmailSuggestion(`${emailParts[0]}@${suggestion}`)
      }
    }
  }

  return (
    <div
      className="grid grid-cols-4 md:grid-cols-6 gap-6"
      data-testid="contact-information-section"
    >
      <div className="col-span-full md:col-span-3">
        <Label htmlFor="email-address-input">Email Address</Label>
        <ControlledTextInput
          name="emails.email"
          type="text"
          inputMode="email"
          error={typeof emailError?.email?.message === 'string'}
          id="email-address-input"
          readOnly={isMulticardFlow}
          onBlur={(e) => handleEmailBlur(e.target.value)}
          autoComplete="email"
        />
        {typeof emailError?.email?.message === 'string' && (
          <TextInputError>{emailError?.email.message}</TextInputError>
        )}
        {!emailError?.email && emailSuggestion && (
          <SuggestionText isError={false}>
            Did you mean{' '}
            <button
              className="underline text-blue-600"
              data-testid="email-suggestion"
              id="email-suggestion"
              onClick={() => {
                track?.(events.EMAIL_VALIDATION.EmailSuggestionAccept)
                clearErrors(['emails.confirmEmail'])
                setValue('emails.email', emailSuggestion)
                setValue('emails.confirmEmail', emailSuggestion)
                setFocus('emails.email')
                setEmailSuggestion('')
              }}
              type="button"
            >
              {emailSuggestion}
            </button>
            ?
          </SuggestionText>
        )}
      </div>
      {!isMulticardFlow && (
        <div className="col-span-full md:col-span-3">
          <Label htmlFor="confirm-email-address-input">Confirm Email Address</Label>
          <ControlledTextInput
            name="emails.confirmEmail"
            type="text"
            inputMode="email"
            error={typeof emailError?.confirmEmail?.message === 'string'}
            id="confirm-email-address-input"
            readOnly={isMulticardFlow}
            autoComplete="email"
          />
          {typeof emailError?.confirmEmail?.message === 'string' && (
            <TextInputError>{emailError?.confirmEmail.message}</TextInputError>
          )}
        </div>
      )}
      <div className="col-span-full md:col-span-3">
        <Label htmlFor="phone-number-input">Personal Phone Number</Label>
        <PhoneInput
          error={typeof phoneNumberError?.message === 'string'}
          readOnly={isMulticardFlow}
        />
        {typeof phoneNumberError?.message === 'string' && (
          <TextInputError data-testid="phone-number-error">
            {phoneNumberError.message}
          </TextInputError>
        )}
      </div>
    </div>
  )
}

export default ContactInformation
